import type { PlatformRole } from '@/types/platform-user.types';
import { cn } from '@/lib/cn';

/**
 * Phase 12.6-4 — shared Platform Users presentation.
 *
 * Design of record: `PLATFORM_USERS_DESIGN.md` v1.0 §5.2, §5.7.
 *
 * §5.7 requires the role label to be a **single source of truth**, because the
 * table, the role filter, the drawer and (in 12.6-5) the role reference card all
 * render role names. Three hand-written copies drift — that is the Companies and
 * Subscriptions precedent, for the same reason.
 *
 * **The raw role key is never rendered.** `platform-operations` is a database
 * value, not end-user language.
 */

/**
 * Human labels for the seven D-041 roles.
 *
 * `Record<PlatformRole, string>` is deliberate: adding a role to the union
 * without labelling it here is a compile error, which is the cheapest possible
 * anti-drift guarantee.
 */
const ROLE_LABEL: Record<PlatformRole, string> = {
  'platform-owner': 'Platform Owner',
  'platform-admin': 'Platform Admin',
  'platform-operations': 'Platform Operations',
  'platform-finance': 'Platform Finance',
  'platform-support': 'Platform Support',
  'platform-developer': 'Platform Developer',
  'platform-auditor': 'Platform Auditor',
};

/**
 * Badge palette.
 *
 * Only `platform-owner` is visually distinguished, because it is the only role
 * that can change anything on this page. The other six are deliberately uniform:
 * colouring six roles differently would imply a hierarchy the matrix does not
 * define — `platform-finance` is not "more" than `platform-support`, it is
 * different.
 */
const ROLE_BADGE: Record<PlatformRole, string> = {
  'platform-owner': 'bg-primary/10 text-primary',
  'platform-admin': 'bg-muted text-foreground',
  'platform-operations': 'bg-muted text-foreground',
  'platform-finance': 'bg-muted text-foreground',
  'platform-support': 'bg-muted text-foreground',
  'platform-developer': 'bg-muted text-foreground',
  'platform-auditor': 'bg-muted text-foreground',
};

/** Narrows an untrusted `role` string from the API to the frozen union. */
export function asPlatformRole(role: string): PlatformRole | null {
  return role in ROLE_LABEL ? (role as PlatformRole) : null;
}

/**
 * The label for a role the API sent.
 *
 * `role` arrives typed as `string` because it is untrusted input — see
 * `platform-user.types.ts`. An unrecognised value renders as itself rather than
 * being hidden or asserted into the union: showing the raw key is ugly, but
 * silently dropping an unknown authority would be worse.
 */
export function roleLabel(role: string): string {
  return asPlatformRole(role) ? ROLE_LABEL[role as PlatformRole] : role;
}

export function RoleBadge({ role, className }: { role: string; className?: string }) {
  const known = asPlatformRole(role);
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
        known ? ROLE_BADGE[known] : 'bg-muted text-muted-foreground',
        className,
      )}
    >
      {roleLabel(role)}
    </span>
  );
}

/**
 * Status badge — §5.2.
 *
 * **Deactivated is muted, NOT destructive-red.** Deactivation is reversible and
 * is the approved offboarding mechanism (OD-B declined delete); painting it red
 * would read as an error or a deletion rather than a normal, undoable state.
 *
 * The state is carried by TEXT as well as colour, so a reader who cannot
 * distinguish the two still reads "Active" or "Deactivated".
 */
export function StatusBadge({ isActive, className }: { isActive: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
        isActive
          ? 'bg-green-500/10 text-green-700 dark:text-green-400'
          : 'bg-muted text-muted-foreground',
        className,
      )}
    >
      {isActive ? 'Active' : 'Deactivated'}
    </span>
  );
}

/** Absolute date and time — the tooltip behind every relative label. */
export function formatUserDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Absolute date only. */
export function formatUserDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Relative time — §5.2 requires "relative with absolute tooltip" for both
 * `lastLoginAt` and `createdAt`.
 *
 * Bounded and honest: no "a moment ago" flourishes, and a future timestamp
 * (clock skew) falls back to the absolute date rather than rendering a negative
 * age.
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 0) return formatUserDate(iso);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  // Beyond a month, a relative label stops being useful — "14mo ago" is harder
  // to reason about than the date itself.
  return formatUserDate(iso);
}

/**
 * `lastLoginAt` — §5.2.
 *
 * **"Never" when null, and that is a real and useful state**, not a missing
 * value: an account that has never been used reads differently from one that
 * simply has no date recorded. It is deliberately NOT an em dash.
 */
export function formatLastSignIn(iso: string | null | undefined): string {
  return iso ? formatRelative(iso) : 'Never';
}

/** The identity line used wherever a user is named in one string. */
export function userIdentityLabel(user: { displayName: string; email: string }): string {
  return `${user.displayName} (${user.email})`;
}
